/* What each built-in register's add/edit form contains.
 *
 * Data, not JSX, for the same reason the custom record types are: a form
 * described as a list of fields is one the shared renderer can draw, and adding
 * a column to a register becomes an entry here rather than a new block of
 * markup in a screen.
 *
 * **Field ids are the model's own property names.** That is load-bearing — the
 * drawer reads values straight off the record and hands back an object that is
 * already a `Partial<T>`, so there is no translation layer between the form and
 * the record and therefore nowhere for a field to quietly go missing. Rename a
 * model property and the form silently stops populating, which is what the
 * render tests are for.
 *
 * Sync-owned columns are deliberately absent. Handover §10 rule 1 says the
 * nightly Function owns them; offering them in a form would mean typing a value
 * that is gone by morning.
 */

import type { IDepot } from '../models';
import type { RenderableField } from '../components/screens/RecordField';
import { traitsFor, type DeviceTraits } from './deviceGroups';

const DEVICE_TYPES = [
  'Laptop',
  'Desktop',
  'Mobile',
  'Tablet',
  'Server',
  'Firewall',
  'Switch',
  'Access point',
  'Printer',
  'Storage',
  'UPS',
];

const RECORD_SOURCES = ['Manual', 'Imported', 'Intune', 'NinjaRMM'];

/* Which device types a field applies to.
 *
 * One form for eleven kinds of thing was one form too general. A switch was
 * being asked for its CPU, its RAM, its storage and which member of staff it is
 * signed out to; a printer for the same. Every one of those boxes is a question
 * with no answer, and a form full of them teaches people to skip fields, which
 * is how the ones that matter end up blank too.
 *
 * Hidden rather than greyed, for the reason the printer fields are: these are
 * not fields that might apply to a switch tomorrow. */
/* One definition of what a type has, shared with the read panel — see
   deviceGroups.ts. The form knowing this and the panel not is what had a
   printer's record asking for its operating system. */
const lacks = (trait: keyof DeviceTraits) => (v: Record<string, unknown>) =>
  !traitsFor(String(v.type ?? ''))[trait];

/** The account kinds, in the order they are offered. Exported so the form and
    the People screen's filter cannot drift apart — they did once already. */
export const ACCOUNT_KINDS = [
  'User',
  'Admin',
  'Service account',
  'Shared mailbox',
  'Licensed mailbox',
  'Resource',
  'Other',
];

/** The sites, offered as a select. Passed in rather than hard-coded because
    they come from the Locations register now, so this list grows when somebody
    opens a depot rather than when somebody edits a type. */
function depotField(depots: readonly IDepot[], required = false, section?: string): RenderableField {
  return {
    section,
    id: 'loc',
    label: 'Depot',
    type: 'select',
    required,
    choices: depots.map((d) => ({ value: d.id, label: d.name })),
  };
}

/* ─────────────────────────── What people usually answer ───────────────────
 *
 * Offered, never enforced. Every one of these fields was free text, and the
 * register that came out of it proves why a dropdown would have been the wrong
 * fix: the real answers are "WPA2-Enterprise (RADIUS via NPS over tunnel)" and
 * "30 days local, 7 years for the exports". A select would have thrown that
 * detail away and left somebody typing it into Notes instead.
 *
 * So these are the usual answers, offered as a list on a box that still takes
 * anything — pick the common one, type the odd one. Where the register already
 * holds better answers than a fixed list could, its own values go first: see
 * valuesSeen below.
 */

const SUPPLIER_SERVICE = [
  'Connectivity', 'Hardware supply', 'Hardware support', 'Software licensing',
  'Print and MFD', 'Telephony', 'Backup and DR', 'Consultancy', 'Consumables',
  'Maintenance contract',
];

const NOTICE_PERIOD = ['None', '30 days', '60 days', '90 days', '3 months', '6 months', '12 months'];

const HOSTING = [
  'On-premise', 'SaaS', 'Microsoft 365', 'SharePoint Online', 'Microsoft Azure',
  'AWS', 'Supplier-hosted', 'Hybrid',
];

const APP_SSO = [
  'Entra SSO', 'Entra federated', 'SAML via Entra', 'Native', 'None — local accounts', 'None',
];

const APP_MFA = [
  'Enforced', 'Enforced by Conditional Access', 'Inherited', 'Optional',
  'Not available', 'None',
];

const DATA_LOCATION = ['UK', 'EU', 'UK / EU', 'M365 tenant', 'United States', 'Global', 'On-premise'];

const DNS_PROVIDER = [
  'Cloudflare', 'GoDaddy', '123-Reg', 'Fasthosts', 'Names.co.uk', 'Route 53',
  'Azure DNS', 'Own nameservers',
];

const DEVICE_MAKE = [
  'HP', 'Dell', 'Lenovo', 'Microsoft', 'Apple', 'Cisco', 'Ubiquiti', 'Netgear',
  'Synology', 'QNAP', 'Fortinet', 'DrayTek', 'APC', 'Brother', 'Canon',
  'Kyocera', 'Xerox', 'Epson', 'Zebra',
];

/** Exported so the bulk bar offers exactly what the form does. */
export const DEVICE_STATUS = [
  'In use', 'Spare — stock', 'Spare — loan pool', 'Built — awaiting collection',
  'With IT for repair', 'Awaiting disposal', 'Retired', 'Lost or stolen',
];

const DEVICE_CPU = [
  'Intel Core i5', 'Intel Core i7', 'Intel Core Ultra 5', 'Intel Core Ultra 7',
  'Intel Xeon Silver', 'Intel Xeon Gold', 'AMD Ryzen 5', 'AMD Ryzen 7', 'Apple M3',
];

const DEVICE_RAM = ['8 GB', '16 GB', '32 GB', '64 GB', '128 GB', '256 GB'];

const DEVICE_DISK = [
  '256 GB SSD', '512 GB SSD', '1 TB SSD', '2 TB SSD', '4 TB SSD',
  '2 × 1 TB SSD RAID 1', '2 × 4 TB HDD RAID 1', '8 TB HDD',
];

const CREDENTIAL_TYPE = [
  'Service account', 'Shared account', 'Local administrator', 'Domain administrator',
  'Break-glass account', 'API key', 'Certificate', 'Licence key', 'Wireless key',
  'Support portal login',
];

const SITE_ROLE = [
  'Head office', 'Depot', 'Depot and training centre', 'Sales office', 'Workshop',
  'Yard', 'Warehouse', 'Remote site',
];

const OPENING_HOURS = [
  'Mon–Fri 08:00–17:00', 'Mon–Fri 07:30–16:30', 'Mon–Fri 08:30–17:00',
  'Mon–Sat 07:00–17:00', '24/7',
];

const WAN_TYPE = [
  'FTTP', 'FTTC', 'Leased line (EAD)', 'EFM', 'ADSL', 'Cable', '4G/5G backup', 'Starlink',
];

const BANDWIDTH = [
  '80/20 Mbps', '100 Mbps', '200/200 Mbps', '300/300 Mbps', '500/500 Mbps', '1/1 Gbps',
];

const ISP = [
  'BT Business', 'Virgin Media Business', 'TalkTalk Business', 'Gamma', 'Zen Internet',
  'Vodafone', 'Daisy', 'Onecom', '34SP.com',
];

const RESOLVERS = ['1.1.1.1, 1.0.0.1', '8.8.8.8, 8.8.4.4', '9.9.9.9, 149.112.112.112'];

const BACKUP_PRODUCT = [
  'Altaro VM Backup (Hornetsecurity)', 'Veeam Backup & Replication',
  'Veeam Data Cloud for Microsoft 365', 'Azure Backup', 'Windows Server Backup',
  'Synology Active Backup', 'Datto', 'Acronis Cyber Protect', 'Host-managed snapshot',
];

const RETENTION = [
  '7 days', '14 days', '30 days', '90 days', '6 months', '12 months', '7 years',
  '30 days local, 90 days offsite',
];

const BACKUP_DEST = [
  'Wasabi eu-west-1', 'Azure Blob (UK South)', 'Local NAS', 'Rotating USB',
  'Tape', 'Supplier cloud',
];

/** What the register already holds for these fields, so the list offered is the
 *  one this place actually uses rather than one guessed in advance.
 *
 *  A make of laptop is a good example: no fixed list beats the eleven makes
 *  already on the register, and the twelfth still gets typed once and then
 *  offered for ever after. */
export function valuesSeen<T extends object>(
  rows: readonly T[] | undefined,
  keys: readonly (keyof T & string)[],
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const k of keys) out[k] = [];
  for (const row of rows ?? []) {
    for (const k of keys) {
      const v = (row as Record<string, unknown>)[k];
      if (typeof v !== 'string') continue;
      const t = v.trim();
      if (t && !out[k].includes(t)) out[k].push(t);
    }
  }
  for (const k of keys) out[k].sort((a, b) => a.localeCompare(b));
  return out;
}

/** The register's own answers first, then the standard ones, no duplicates.
 *
 *  Order is not incidental: a datalist shows its options in document order, so
 *  what this place actually uses has to come first or the standard list buries
 *  it. Exported for the test that holds that. */
export function offer(...lists: (readonly string[] | undefined)[]): string[] {
  const out: string[] = [];
  for (const list of lists) {
    for (const v of list ?? []) if (!out.includes(v)) out.push(v);
  }
  return out;
}

export function supplierFields(seen?: Record<string, string[]>): RenderableField[] {
  return [
    { id: 'name', label: 'Supplier', type: 'text', required: true },
    { id: 'service', label: 'Service provided', type: 'text', suggest: offer(seen?.service, SUPPLIER_SERVICE) },
    {
      id: 'acct',
      label: 'Account number',
      type: 'text',
    },
    { id: 'support', label: 'Support line', type: 'text' },
    { id: 'portal', label: 'Portal', type: 'url' },
    { id: 'start', label: 'Contract start', type: 'date' },
    { id: 'end', label: 'Contract end', type: 'date' },
    {
      id: 'notice',
      label: 'Notice period',
      type: 'text',
      hint: '"90 days", "3 months" or "None". Counted back from the contract end.',
      suggest: offer(seen?.notice, NOTICE_PERIOD),
    },
    { id: 'cost', label: 'Annual cost', type: 'money' },
    { id: 'owner', label: 'Owner', type: 'person' },
    { id: 'auto', label: 'Renews automatically', type: 'check' },
    { id: 'notes', label: 'Notes', type: 'textarea' },
  ];
}

export function applicationFields(seen?: Record<string, string[]>): RenderableField[] {
  return [
    { id: 'name', label: 'Application', type: 'text', required: true },
    { id: 'purpose', label: 'Purpose', type: 'text' },
    /* The supplier register's own names, offered — deliberately not a
       type: 'supplier' picker.
     *
     * That control stores a record id, and every application on the register
     * already holds a name here. Switching would have left the column holding
     * both, which reads correctly on screen (the renderer falls back to the raw
     * value) and is a trap for anything that ever tries to join on it.
     *
     * A list of the same names costs one click either way and keeps the column
     * meaning one thing. */
    { id: 'supplier', label: 'Supplier', type: 'text', suggest: offer(seen?.supplier) },
    {
      id: 'crit',
      label: 'Criticality',
      type: 'select',
      required: true,
      /* Stored as 1|2|3 and shown in words. The value is a string here because
         a select's value always is; the screen coerces it back on save. */
      choices: [
        { value: '1', label: '1 — Critical' },
        { value: '2', label: '2 — Important' },
        { value: '3', label: '3 — Useful' },
      ],
    },
    { id: 'bizOwner', label: 'Business owner', type: 'person' },
    { id: 'itOwner', label: 'IT owner', type: 'person' },
    { id: 'users', label: 'Users', type: 'number' },
    { id: 'host', label: 'Hosting', type: 'text', suggest: offer(seen?.host, HOSTING) },
    {
      id: 'sso',
      label: 'SSO',
      type: 'text',
      hint: '"None" if there is none.',
      suggest: offer(seen?.sso, APP_SSO),
    },
    { id: 'mfa', label: 'MFA', type: 'text', suggest: offer(seen?.mfa, APP_MFA) },
    { id: 'data', label: 'Data location', type: 'text', suggest: offer(seen?.data, DATA_LOCATION) },
    { id: 'renews', label: 'Renews', type: 'date' },
    { id: 'cost', label: 'Annual cost', type: 'money' },
    { id: 'admin', label: 'Admin portal', type: 'url' },
    { id: 'notes', label: 'Notes', type: 'textarea' },
  ];
}

/* What is left to type. Six of the ten fields moved to the nightly sync when
   RDAP, TLS and DNS turned out to publish them, and a form offering a synced
   column is a form that quietly loses what you put in it. */
export function domainFields(seen?: Record<string, string[]>): RenderableField[] {
  return [
    {
      id: 'name',
      label: 'Domain',
      type: 'text',
      required: true,
      hint: 'The registrar, expiry, certificate and mail records are read overnight.',
    },
    {
      id: 'dns',
      label: 'DNS provider',
      type: 'text',
      hint: 'Seeded from the nameservers. Correct it and the sync leaves it alone.',
      suggest: offer(seen?.dns, DNS_PROVIDER),
    },
    {
      id: 'dkim',
      label: 'DKIM selectors',
      type: 'textarea',
      hint: 'Found overnight where the selectors are standard. Add an unusual one and the sync keeps it.',
    },
    { id: 'notes', label: 'Notes', type: 'textarea' },
  ];
}

/* Grouped, and the groups have to stay contiguous: the renderer emits a heading
   only when the section changes, so a field out of order prints its heading a
   second time. Ordered the way somebody fills one in — what it is, what is in
   it, what it cost, where it lives, and how we know. */
/** @param types Which device types the Type box offers. Narrowed per screen, so
 *  adding a switch from Infrastructure cannot accidentally produce a laptop
 *  that then appears on a different list than the one you added it from. */
export function deviceFields(
  depots: readonly IDepot[],
  types: readonly string[] = DEVICE_TYPES,
  /* What the register already holds for these boxes, from valuesSeen. The best
     list of makes is the makes already on it; the twelfth gets typed once and
     is then offered for ever. */
  seen?: Record<string, string[]>,
): RenderableField[] {
  return [
    { section: 'Identity', id: 'tag', label: 'Asset tag', type: 'text', required: true },
    { section: 'Identity', id: 'type', label: 'Type', type: 'select', required: true, options: [...types] },
    {
      section: 'Identity',
      id: 'serial',
      label: 'Serial number',
      type: 'text',
      hint: 'What both syncs match on.',
    },

    { section: 'Hardware', id: 'make', label: 'Make', type: 'text', suggest: offer(seen?.make, DEVICE_MAKE) },
    { section: 'Hardware', id: 'model', label: 'Model', type: 'text', suggest: offer(seen?.model) },
    /* A switch has a processor and nobody has ever needed to know what it is.
       These are synced from NinjaOne for the machines it manages, which is the
       same set. */
    { section: 'Hardware', id: 'cpu', label: 'CPU', type: 'text', hiddenWhen: lacks('spec'), suggest: offer(seen?.cpu, DEVICE_CPU) },
    { section: 'Hardware', id: 'ram', label: 'RAM', type: 'text', hiddenWhen: lacks('spec'), suggest: offer(seen?.ram, DEVICE_RAM) },
    { section: 'Hardware', id: 'disk', label: 'Storage', type: 'text', hiddenWhen: lacks('spec'), suggest: offer(seen?.disk, DEVICE_DISK) },

    /* Owned or hired decides which four of the next six apply. Printers are all
       hired at Hird, and asking a hired one for a purchase date and a warranty
       leaves two blank boxes on every record. */
    {
      section: 'Ownership',
      id: 'ownership',
      label: 'Owned or hired',
      type: 'select',
      options: ['Purchased', 'Hired'],
      hint: 'Blank counts as purchased.',
    },

    {
      section: 'Ownership',
      id: 'bought',
      label: 'Purchase date',
      type: 'date',
      hiddenWhen: (v) => v.ownership === 'Hired',
    },
    {
      section: 'Ownership',
      id: 'warranty',
      label: 'Warranty ends',
      type: 'date',
      hiddenWhen: (v) => v.ownership === 'Hired',
      hint: 'Not synced. Enter it here.',
    },

    {
      section: 'Ownership',
      id: 'hireSupplier',
      label: 'Hired from',
      type: 'text',
      hiddenWhen: (v) => v.ownership !== 'Hired',
      suggest: offer(seen?.hireSupplier),
    },
    {
      section: 'Ownership',
      id: 'hireRef',
      label: 'Contract reference',
      type: 'text',
      hiddenWhen: (v) => v.ownership !== 'Hired',
      hint: 'What they ask for on the phone.',
    },
    {
      section: 'Ownership',
      id: 'hireEnds',
      label: 'Hire ends',
      type: 'date',
      hiddenWhen: (v) => v.ownership !== 'Hired',
      hint: 'Read by the expiries screen.',
    },
    {
      section: 'Ownership',
      id: 'hireCost',
      label: 'Cost a month',
      type: 'money',
      hiddenWhen: (v) => v.ownership !== 'Hired',
    },

    depotField(depots, true, 'Placement'),
    { section: 'Placement', id: 'ip', label: 'IP address', type: 'text' },
    { section: 'Placement', id: 'status', label: 'Status', type: 'text', suggest: offer(seen?.status, DEVICE_STATUS) },
    /* Infrastructure is not signed out to anybody. A firewall belongs to the
       building, and naming a person against it makes a leaver's offboarding
       list report kit nobody has to hand back. */
    {
      section: 'Placement',
      id: 'user',
      label: 'Assigned to',
      type: 'person',
      hiddenWhen: lacks('assignable'),
    },

    /* Only on a printer. Not greyed — a laptop does not have a toner code the
       way a per-user licence has seats; the field is not disabled there, it does
       not exist. */
    ...managementFields(seen),
    ...printerFields(seen),

    { section: 'Record', id: 'src', label: 'Record source', type: 'select', options: RECORD_SOURCES },
    { section: 'Record', id: 'notes', label: 'Notes', type: 'textarea' },
  ];
}

/* The lights-out controller, for the kit that has one.
 *
 * Shown on servers and storage only. A switch or a firewall is reached at the
 * address already in the IP field; a server is the case where there are two,
 * and where the second is the one that matters precisely when the first has
 * stopped answering. */
function managementFields(seen?: Record<string, string[]>): RenderableField[] {
  const onlyManaged = lacks('lightsOut');
  return [
    {
      section: 'Management',
      id: 'mgmtType',
      label: 'Out-of-band management',
      type: 'select',
      options: ['iLO', 'iDRAC', 'XCC', 'IPMI', 'Other'],
      hiddenWhen: onlyManaged,
      hint: 'iLO on HP, iDRAC on Dell, XCC on Lenovo.',
    },
    {
      section: 'Management',
      id: 'mgmtAddress',
      label: 'Management address',
      type: 'text',
      hiddenWhen: onlyManaged,
      hint: "The controller’s own address, not the server’s.",
    },
    {
      section: 'Management',
      id: 'mgmtCredential',
      label: 'Credential',
      type: 'text',
      hiddenWhen: onlyManaged,
      hint: 'The name of the entry in Credentials, never the password.',
      suggest: offer(seen?.mgmtCredential),
    },
  ];
}

/* What a printer needs beyond being a device.
 *
 * Six fields, and the first is the one that earns the section: nobody looks up a
 * printer to read its serial, they look it up because it has run out of
 * something and they need the cartridge code before ringing the supplier. */
function printerFields(seen?: Record<string, string[]>): RenderableField[] {
  const onlyPrinters = lacks('printer');
  return [
    {
      section: 'Printer',
      id: 'consumables',
      label: 'Cartridges and toner',
      type: 'textarea',
      hiddenWhen: onlyPrinters,
      hint: 'The codes to order, not the colours \u2014 "TN-423BK / C / M / Y".',
    },
    {
      section: 'Printer',
      id: 'placement',
      label: 'Where it is',
      type: 'text',
      hiddenWhen: onlyPrinters,
      hint: 'Floor, room or bay.',
      suggest: offer(seen?.placement),
    },
    {
      section: 'Printer',
      id: 'printQueue',
      label: 'Print queue',
      type: 'text',
      hiddenWhen: onlyPrinters,
      hint: 'The shared name and the server it is shared from.',
      suggest: offer(seen?.printQueue),
    },
    {
      section: 'Printer',
      id: 'webUI',
      label: 'Web interface',
      type: 'url',
      hiddenWhen: onlyPrinters,
      hint: 'Where the real toner level and page count live.',
    },
    {
      section: 'Printer',
      id: 'meterReading',
      label: 'Meter reading',
      type: 'number',
      hiddenWhen: onlyPrinters,
      hint: 'Page count when it was last read.',
    },
    {
      section: 'Printer',
      id: 'meterDate',
      label: 'Meter read on',
      type: 'date',
      hiddenWhen: onlyPrinters,
      hint: 'A meter with no date is a number.',
    },
  ];
}

export function personFields(
  depots: readonly IDepot[],
  seen?: Record<string, string[]>,
): RenderableField[] {
  return [
    { section: 'Identity', id: 'name', label: 'Display name', type: 'text', required: true },
    {
      section: 'Identity',
      id: 'upn',
      label: 'Sign-in address',
      type: 'text',
      required: true,
      hint: 'The key the nightly Entra sync matches on.',
    },

    { section: 'Contact', id: 'mobile', label: 'Mobile', type: 'text' },

    { section: 'Role', id: 'title', label: 'Job title', type: 'text', suggest: offer(seen?.title) },
    { section: 'Role', id: 'dept', label: 'Department', type: 'text', suggest: offer(seen?.dept) },
    {
      ...depotField(depots, true, 'Role'),
      /* Only a person works somewhere. A shared mailbox, scanner or service
         account has no depot, and onboarding already says so ("has no depot, no
         laptop") — this form was the one place still demanding one. Blank kind
         stays required: it is unclassified, and may turn out to be somebody. */
      requiredUnless: (v) => { const k = String(v.kind ?? '').trim(); return k !== '' && k !== 'User'; },
    },
    { section: 'Role', id: 'start', label: 'Start date', type: 'date' },

    {
      section: 'Account',
      id: 'status',
      label: 'Status',
      type: 'select',
      options: ['Active', 'Starter', 'Leaver'],
      hint: 'For a User, Leaver follows sign-in being blocked in Entra. For any other account type the status is yours, and the sync leaves it alone.',
    },
    {
      section: 'Account',
      id: 'kind',
      label: 'Account type',
      type: 'select',
      options: ACCOUNT_KINDS,
      hint: 'Blank counts as unclassified and is left out of head-counts.',
    },

    {
      section: 'Housekeeping',
      id: 'leavingOn',
      label: 'Last day',
      type: 'date',
      hint: 'Set this in advance. Status only turns to Leaver once the account has already been disabled, so this is the only thing that puts somebody in the leaver queue while there is still time to act.',
    },
    {
      section: 'Housekeeping',
      id: 'keepUntil',
      label: 'Keep the account until',
      type: 'date',
      hint: 'Leavers only. Blank means ninety days after they left. Nothing is ever deleted \u2014 this only says when it is safe to.',
    },
    { section: 'Housekeeping', id: 'notes', label: 'Notes', type: 'textarea' },
  ];
}

export function groupFields(): RenderableField[] {
  return [
    {
      id: 'purpose',
      label: 'What this group is for',
      type: 'textarea',
      hint: 'What the group opens, who asked for it, and whether it should still exist.',
    },
  ];
}

export function licenceFields(): RenderableField[] {
  return [
    {
      id: 'short',
      label: 'Short name',
      type: 'text',
      hint: 'What to call it. The synced name is the part number.',
    },
    {
      id: 'cost',
      label: 'Cost per seat, per month',
      type: 'money',
      hint: 'Every money figure on this screen comes from here.',
    },
    { id: 'renews', label: 'Term ends', type: 'date' },
    {
      id: 'basis',
      label: 'How seats are taken up',
      type: 'select',
      options: ['Per user', 'Per device'],
      hint: 'Blank means per user.',
    },
    {
      id: 'seatsUsed',
      label: 'Seats in use',
      type: 'number',
      /* Off unless the licence is per device. On a per-user one the holders are
         the answer, and a typed number would be a second one that could
         disagree with it. */
      disabledWhen: (v) => v.basis !== 'Per device',
      hint: 'Per-device licences only. Counted from the holders on a per-user licence.',
    },
    {
      id: 'hidden',
      label: 'Hide from the register',
      type: 'check',
      hint: 'Keeps the row, but drops it from every total, the renewal list and the sidebar count.',
    },
    {
      id: 'tone',
      label: 'Tone',
      type: 'select',
      options: ['info', 'ok', 'warn', 'bad', 'muted', 'purple'],
      hint: 'How this licence reads in a list of badges.',
    },
  ];
}

/** A backup job, minus the two columns a script pushes.
 *
 *  Last run and whether it succeeded are absent on purpose (§11): a green tick
 *  somebody typed records that a person believed it ran, not that it did.
 */
export function backupFields(seen?: Record<string, string[]>): RenderableField[] {
  return [
    { section: 'What it is', id: 'name', label: 'Job', type: 'text', required: true },
    {
      section: 'What it covers',
      id: 'devices',
      label: 'Servers and infrastructure',
      type: 'multi',
      from: 'devices',
      hint: 'Servers and infrastructure only.',
    },
    {
      section: 'What it covers',
      id: 'apps',
      label: 'Applications',
      type: 'multi',
      from: 'applications',
    },
    {
      section: 'What it covers',
      id: 'what',
      label: 'Anything else',
      type: 'textarea',
      hint: 'Anything that is not a record here \u2014 a mailbox, a website, a folder.',
    },
    {
      section: 'What it is',
      id: 'product',
      label: 'Product',
      type: 'text',
      hint: 'The software doing it.',
      suggest: offer(seen?.product, BACKUP_PRODUCT),
    },
    {
      section: 'What it is',
      id: 'method',
      label: 'Method',
      type: 'select',
      options: [
        'Image-level',
        'Application-aware',
        'Agent',
        'Snapshot',
        'Replication',
        'Export or dump',
        'SaaS — vendor hosted',
        'None',
      ],
    },

    {
      section: 'How it runs',
      id: 'sched',
      label: 'Schedule',
      type: 'select',
      options: [
        'Continuous',
        'Every 15 minutes',
        'Hourly',
        'Every 4 hours',
        'Nightly',
        'Weekly',
        'Monthly',
        'On demand only',
      ],
    },
    { section: 'How it runs', id: 'ret', label: 'Retention', type: 'text', suggest: offer(seen?.ret, RETENTION) },
    {
      section: 'How it runs',
      id: 'dest',
      label: 'Destination',
      type: 'text',
      hint: 'Where the copies land.',
      suggest: offer(seen?.dest, BACKUP_DEST),
    },
    {
      section: 'How it runs',
      id: 'alerts',
      label: 'Failure alerts to',
      type: 'text',
      hint: 'Who hears about it when it fails.',
      suggest: offer(seen?.alerts),
    },

    {
      section: 'Targets',
      id: 'rpo',
      label: 'RPO',
      type: 'select',
      options: ['15 minutes', '1 hour', '4 hours', '12 hours', '24 hours', '1 week'],
      hint: 'How much data you can afford to lose.',
    },
    {
      section: 'Targets',
      id: 'rto',
      label: 'RTO',
      type: 'select',
      options: ['1 hour', '4 hours', '8 hours', '24 hours', '3 days', '1 week'],
      hint: 'How long you can afford to be without it.',
    },

    { section: 'Protection', id: 'offsite', label: 'Held offsite', type: 'check' },
    {
      section: 'Protection',
      id: 'immutable',
      label: 'Immutable',
      type: 'check',
      hint: 'Whether ransomware on the source could delete this copy too.',
    },
    { section: 'Protection', id: 'encrypted', label: 'Encrypted at rest', type: 'check' },
    {
      section: 'Protection',
      id: 'tested',
      label: 'Last proven restore',
      type: 'date',
    },
    {
      section: 'Ownership',
      id: 'crit',
      label: 'Criticality',
      type: 'select',
      choices: [
        { value: '1', label: '1 — Critical' },
        { value: '2', label: '2 — Important' },
        { value: '3', label: '3 — Useful' },
      ],
    },
    { section: 'Ownership', id: 'owner', label: 'Owner', type: 'person' },
    { section: 'Ownership', id: 'notes', label: 'Notes', type: 'textarea' },
  ];
}

/** A credential record — the index, and only the index.
 *
 *  §16: no secret values, ever, under any framing. There is no value field here
 *  and there is no value column behind it; the provisioning script fails if one
 *  ever appears. What this holds is a *reference* — which vault and which secret
 *  name — so the app can point at a secret without ever having read one.
 */
export function credentialFields(seen?: Record<string, string[]>): RenderableField[] {
  return [
    { id: 'name', label: 'What it is for', type: 'text', required: true },
    { id: 'system', label: 'System', type: 'text', suggest: offer(seen?.system) },
    {
      id: 'type',
      label: 'Account type',
      type: 'text',
      hint: 'Service account, API key, shared login, certificate…',
      suggest: offer(seen?.type, CREDENTIAL_TYPE),
    },
    {
      id: 'vault',
      label: 'Vault reference',
      type: 'text',
      hint: 'Vault and secret name. Never the secret itself.',
    },
    { id: 'owner', label: 'Owner', type: 'person' },
    { id: 'rotated', label: 'Last rotated', type: 'date' },
    {
      id: 'every',
      label: 'Rotate every, in days',
      type: 'number',
      hint: 'Drives the rotation reminder. 365 if you have no policy.',
    },
    { id: 'notes', label: 'Notes', type: 'textarea' },
  ];
}

/** The circuit half of a depot's network record. Subnets, SSIDs and tunnels are
    nested lists and are edited separately. */
/* One per site. Shaped on IT Glue's Locations, which is the part of it that
   works well: an address, a way to ring the place, who to ask, and enough about
   the building for somebody who has never been there. */
export function locationFields(seen?: Record<string, string[]>): RenderableField[] {
  return [
    {
      section: 'Address',
      id: 'short',
      label: 'Short code',
      type: 'text',
      hint: 'Three letters, for badges and labels. Blank uses the first three of the name.',
    },
    { section: 'Address', id: 'addr', label: 'Address', type: 'textarea', required: true },
    { section: 'Address', id: 'postcode', label: 'Postcode', type: 'text' },
    { section: 'Address', id: 'phone', label: 'Main number', type: 'text' },
    {
      section: 'Address',
      id: 'role',
      label: 'What the site is',
      type: 'text',
      hint: 'Head office, depot, training centre, sales office.',
      suggest: offer(seen?.role, SITE_ROLE),
    },

    { section: 'Who to ask', id: 'contact', label: 'Site contact', type: 'person' },
    {
      section: 'Who to ask',
      id: 'hours',
      label: 'Opening hours',
      type: 'text',
      hint: 'When somebody is there to let an engineer in.',
      suggest: offer(seen?.hours, OPENING_HOURS),
    },

    {
      section: 'The building',
      id: 'comms',
      label: 'Comms room',
      type: 'textarea',
      hint: 'Where the kit lives.',
    },
    {
      section: 'The building',
      id: 'access',
      label: 'Access',
      type: 'textarea',
      hint: 'Who holds keys, who responds to the alarm, out-of-hours arrangements. Never a code or a combination.',
    },
    { section: 'The building', id: 'notes', label: 'Notes', type: 'textarea' },
  ];
}

export function networkFields(seen?: Record<string, string[]>): RenderableField[] {
  return [
    { section: 'Circuit', id: 'provider', label: 'Provider', type: 'text', suggest: offer(seen?.provider, ISP) },
    {
      section: 'Circuit',
      id: 'wan',
      label: 'Circuit reference',
      type: 'text',
      hint: 'What the provider answers to when you ring. Not the account number.',
    },
    { section: 'Circuit', id: 'wanType', label: 'Type', type: 'text', hint: 'FTTP, FTTC, leased line, EFM.', suggest: offer(seen?.wanType, WAN_TYPE) },
    { section: 'Circuit', id: 'speed', label: 'Bandwidth', type: 'text', hint: 'Contracted, down/up.', suggest: offer(seen?.speed, BANDWIDTH) },
    { section: 'Circuit', id: 'block', label: 'Public IP block', type: 'text' },

    { section: 'Support', id: 'wanAcct', label: 'Account number', type: 'text' },
    { section: 'Support', id: 'wanSupport', label: 'Support line', type: 'text' },
    { section: 'Support', id: 'wanEnds', label: 'Contract ends', type: 'date' },

    {
      section: 'Resilience',
      id: 'failover',
      label: 'Failover',
      type: 'textarea',
      hint: 'What happens when the circuit drops \u2014 4G backup, a second line, or nothing.',
    },
    {
      section: 'Resilience',
      id: 'dns',
      label: 'DNS servers',
      type: 'text',
      hint: 'The site default.',
      suggest: offer(seen?.dns, RESOLVERS),
    },
  ];
}

/** The device boxes worth offering the register's own answers for.
 *
 *  Shared by Devices, Infrastructure and Printers so the three lists cannot
 *  drift into offering different suggestions for the same box. */
export const DEVICE_SEEN = [
  'make', 'model', 'cpu', 'ram', 'disk', 'status',
  'hireSupplier', 'placement', 'printQueue', 'mgmtCredential',
] as const;
